import React, { useEffect } from 'react';
import { connect } from '../../../redux/store';
import {
  actions as serviceActions,
  selectIsServiceOwner,
} from '../../../redux/modules/settingsService';
import { UncontrolledTooltip } from 'reactstrap';
import { I18n } from '@kineticdata/react';
import { Link } from '@reach/router';
import { PageTitle } from '../../shared/PageTitle';
import {
  Card,
  CardCol,
  CardRow,
  EmptyMessage,
  ErrorMessage,
  LoadingMessage,
  TimeAgo,
  Utils,
  C,
  addToast,
  addToastAlert,
  openConfirm,
  openModalForm,
  selectServicesKappSlug,
} from '@kineticdata/bundle-common';
import {
  isActiveClass,
  getActionPath,
  getCatalogRevisionLockData,
} from '../../../utils';
import { ActionCard } from '../../actions/ActionCard';
import { ContentManager } from '../../shared/content_manager/ContentManager';
import {
  CONTENT_MANAGER_MODE,
  getStatusColor,
} from '../../shared/content_manager/helpers';
import { List, Map } from 'immutable';

const ServiceComponent = ({
  portfolioId,
  serviceId,
  mode,
  // Selected service
  service,
  serviceContentReady,
  serviceError,
  serviceLoading,
  // List of all revisions for the selected service id
  revisions,
  // Actions for the selected portfolio
  actions,
  // All portfolios
  portfoliosNameMap,
  // Are user actions pending
  actionsPending,
  // Actions
  fetchService,
  cloneService,
  expireService,
  deleteServiceRevision,
  // Other props
  kappSlug,
  profile,
  isOwner,
  navigate,
  appLocation,
  isMobile,
}) => {
  // Fetch the current service on mount
  useEffect(
    () => {
      fetchService(serviceId);
    },
    [fetchService, serviceId],
  );

  const isServiceDraft =
    !!service && service.values && service.values[C.FIELD_STATUS] === 'Draft';
  const isServiceActive =
    !!service && service.values && service.values[C.FIELD_STATUS] === 'Active';
  const doesServiceHaveChildren =
    !!service && service.children && service.children.length > 0;

  const handleCreateChangeRequest = () =>
    openModalForm({
      formSlug: C.FORM_SLUG_CATALOG_CHANGE_REQUEST,
      kappSlug: kappSlug,
      title: 'Request Change',
      values: {
        'Catalog ID': serviceId,
        'Catalog Type': 'service',
        'Catalog Submission ID': service.id,
        'Assigned Teams': service.values['Technical Owners'],
      },
      confirmationMessage: 'Your change request has been submitted.',
      duration: 5000,
    });

  const handleDeleteService = () => {
    // If there is a revision, show alert that revsision needs to first be deleted
    if (revisions.size > 0 && !isServiceDraft) {
      addToastAlert({
        severity: 'warning',
        title: 'Cannot delete service with existing revisions',
        message:
          'Please delete any draft revisions or wait for pending revisions to be completed before deleting the service.',
      });
    }
    // Otherwise delete or expire the service
    else {
      openConfirm({
        title: 'Delete Service',
        body: (
          <div>
            <span className="d-block mb-3">
              Are you sure you want to delete this service?
            </span>
            <em>
              This action is immediate, does not go through an approval process,
              and cannot be undone.
            </em>
          </div>
        ),
        actionName: 'Delete',
        ok: () =>
          // If service is a draft, delete it, otherwise expire it
          isServiceDraft
            ? deleteServiceRevision({
                submissionId: service.id,
                cancel: doesServiceHaveChildren,
                success: submission => {
                  navigate(`..${!!mode ? '/..' : ''}`);
                  addToast('Service successfully deleted');
                },
                failure: error => {
                  addToastAlert({
                    severity: 'danger',
                    title: 'Failed to delete service',
                    message: error.message,
                  });
                },
              })
            : expireService({
                submissionId: service.id,
                success: submission => {
                  navigate(`..${!!mode ? '/..' : ''}`);
                  addToast('Service successfully deleted');
                },
                failure: error => {
                  addToastAlert({
                    severity: 'danger',
                    title: 'Failed to delete service',
                    message: error.message,
                  });
                },
              }),
      });
    }
  };

  const handleInactivateService = () => {
    openConfirm({
      title: 'Inactivate Service',
      body: (
        <div>
          <span className="d-block mb-3">
            Are you sure you want to inactivate this service? This will remove
            the active record and{' '}
            {revisions.size > 0
              ? 'leave the current revision in its place.'
              : 'create a new revision record in draft status.'}
          </span>
          <em>
            This action is immediate, does not go through an approval process,
            and cannot be undone.
          </em>
        </div>
      ),
      actionName: 'Inactivate',
      ok: () =>
        expireService({
          submissionId: service.id,
          clone: revisions.size === 0,
          refetchItem: true,
          success: submission => {
            navigate(!!mode ? '..' : '');
            addToast('Service successfully inactivated');
          },
          failure: error => {
            addToastAlert({
              severity: 'danger',
              title: 'Failed to inactivate service',
              message: error.message,
            });
          },
        }),
    });
  };

  return (
    <div className="page-container">
      <div className="page-panel">
        <PageTitle
          className="page-title-heading--sm"
          parts={[
            service && service.values['Name'],
            portfolioId && portfoliosNameMap.get(portfolioId),
            'Catalog Management',
          ]}
          suptitle="SERVICE"
          title={service && service.values['Name']}
          breadcrumbs={[
            {
              label: (
                <span>
                  <span className="fa fa-fw fa-md fa-arrow-left text-muted" />
                  <I18n>{C.SETTINGS_LABEL}</I18n>
                </span>
              ),
              to: '/settings',
            },
            {
              label: 'Catalog Management',
              to: `${appLocation}/settings/catalog/${
                portfolioId ? 'portfolios' : 'services'
              }`,
            },
            portfolioId && {
              label: portfoliosNameMap.get(portfolioId) || portfolioId,
              to: `${appLocation}/settings/catalog/portfolios/${portfolioId}`,
            },
          ]}
          actions={
            !serviceError &&
            service && [
              {
                icon: !serviceLoading ? 'sync' : 'sync fa-spin',
                aria: 'Reload Service',
                onClick: () => {
                  fetchService(serviceId);
                },
              },
              profile.isSMO && {
                label: 'Request Change',
                onClick: handleCreateChangeRequest,
                menu: true,
              },
              isOwner &&
                revisions.size === 0 && {
                  menu: true,
                  label: 'Start Revision',
                  onClick: () =>
                    cloneService({
                      submissionId: service.id,
                      success: submission => {
                        navigate(`revision/${submission.id}`);
                      },
                      failure: error => {
                        addToastAlert({
                          severity: 'danger',
                          title: 'Failed to start a revision',
                          message: error.message,
                        });
                      },
                    }),
                  disabled: actionsPending,
                },
              profile.isSMO &&
                isServiceActive && {
                  label: 'Inactivate Service',
                  onClick: handleInactivateService,
                  className: 'text-danger',
                  disabled: actionsPending,
                  menu: true,
                  divider: true,
                },
              profile.isSMO && {
                label: 'Delete Service',
                onClick: handleDeleteService,
                className: 'text-danger',
                disabled: actionsPending,
                menu: true,
                divider: !isServiceActive,
              },
            ]
          }
        />

        {serviceError ? (
          <ErrorMessage
            title="Failed to retrieve service."
            message={serviceError.message}
          />
        ) : !serviceContentReady && serviceLoading ? (
          <LoadingMessage />
        ) : (
          !!service && (
            <>
              {service.values['Status'] !== 'Active' && (
                <div className="alert alert-info alert-bar mb-3">
                  <I18n>This is a new service that is not yet published.</I18n>{' '}
                  {isOwner && (
                    <I18n>
                      Go to the revision tab to continue editing this service.
                    </I18n>
                  )}
                </div>
              )}

              <ul className="nav nav-tabs h6" role="tablist">
                <li role="tab" className="nav-item">
                  <Link
                    to={!mode ? '.' : '..'}
                    getProps={isActiveClass('nav-link')}
                  >
                    <I18n>Service</I18n>
                  </Link>
                </li>
                {revisions.size > 0 && (
                  <li role="tab" className="nav-item">
                    <Link
                      to={!mode ? 'revision' : '../revision'}
                      getProps={isActiveClass('nav-link')}
                    >
                      <I18n>Revision</I18n>
                    </Link>
                  </li>
                )}
                {actions.size > 0 && (
                  <li role="tab" className="nav-item">
                    <Link
                      to={!mode ? 'actions' : '../actions'}
                      getProps={isActiveClass('nav-link')}
                    >
                      <I18n>Change Requests</I18n>
                    </Link>
                  </li>
                )}
              </ul>

              {!mode && (
                <div className="nav-tabs--content">
                  {service && serviceContentReady ? (
                    <ContentManager
                      submission={service}
                      mode={CONTENT_MANAGER_MODE.MANAGE}
                      username={profile.username}
                    />
                  ) : (
                    <ErrorMessage title="Failed to retrieve service content." />
                  )}
                </div>
              )}

              {mode === 'revision' && (
                <div className="nav-tabs--content">
                  {revisions.size > 0 ? (
                    <div className="cards mb-4">
                      {revisions.map(r => {
                        const approval = List(r.children)
                          .filter(c => c.type === C.APPROVAL_FORM_TYPE)
                          .sortBy(a => a.updatedAt)
                          .reverse()
                          .first();
                        const isRevisionOwner = selectIsServiceOwner(
                          profile,
                          r,
                        );

                        // Get lock info for revision
                        const {
                          isLocked,
                          isLockedByMe,
                        } = getCatalogRevisionLockData(r, profile.username);

                        return (
                          <Card key={r.id}>
                            <CardCol>
                              <CardRow type="title">
                                {isLocked &&
                                  !isLockedByMe && (
                                    <>
                                      <span
                                        id={`lock-${r.id}`}
                                        className="fa fa-lock fa-fw"
                                      />
                                      <UncontrolledTooltip
                                        placement="right"
                                        target={`lock-${r.id}`}
                                        delay={0}
                                        trigger="hover focus"
                                      >
                                        <I18n>
                                          This revision is currently locked by
                                          another user.
                                        </I18n>
                                      </UncontrolledTooltip>
                                    </>
                                  )}
                                <span>
                                  <I18n>Revision</I18n> {r.handle}
                                </span>
                                <div className="ml-auto">
                                  <Link className="btn btn-primary" to={r.id}>
                                    <I18n>
                                      {r.values['Status'] === 'Draft' &&
                                      isRevisionOwner
                                        ? 'Continue Editing'
                                        : 'Review Revision'}
                                    </I18n>
                                    <span className="fa fa-arrow-right fa-fw" />
                                  </Link>
                                </div>
                              </CardRow>
                              <CardRow className="text-muted" type="meta">
                                <dl className="flat align-items-center">
                                  <div>
                                    <dd>
                                      <span
                                        className={`badge badge-${getStatusColor(
                                          r.values['Status'],
                                        )} badge-stylized text-muted`}
                                      >
                                        <I18n>{r.values['Status']}</I18n>
                                      </span>
                                    </dd>
                                  </div>
                                  <div>
                                    <dt>
                                      <I18n>Created</I18n>
                                    </dt>
                                    <dd>
                                      <TimeAgo timestamp={r.createdAt} />
                                    </dd>
                                  </div>
                                  {r.coreState === 'Submitted' ? (
                                    <div>
                                      <dt>
                                        <I18n>Submitted</I18n>
                                      </dt>
                                      <dd>
                                        <TimeAgo timestamp={r.submittedAt} />
                                      </dd>
                                    </div>
                                  ) : (
                                    <div>
                                      <dt>
                                        <I18n>Updated</I18n>
                                      </dt>
                                      <dd>
                                        <TimeAgo timestamp={r.updatedAt} />
                                      </dd>
                                    </div>
                                  )}
                                </dl>
                              </CardRow>
                              {approval && (
                                <CardRow
                                  className="text-muted mt-1"
                                  type="meta"
                                >
                                  <dl className="flat align-items-center">
                                    {approval.coreState === 'Submitted' && (
                                      <div>
                                        <dt>
                                          <I18n>Approval Decision</I18n>
                                        </dt>
                                        <dd>
                                          <I18n>
                                            {approval.values['Approved']}
                                          </I18n>
                                        </dd>
                                      </div>
                                    )}
                                    {approval.values[
                                      C.FIELD_ASSIGNED_INDIVIDUAL_NAME
                                    ] && (
                                      <div>
                                        <dt>
                                          <I18n>Approvers</I18n>
                                        </dt>
                                        <dd>
                                          <I18n>
                                            {
                                              approval.values[
                                                C.FIELD_ASSIGNED_INDIVIDUAL_NAME
                                              ]
                                            }
                                          </I18n>
                                        </dd>
                                      </div>
                                    )}
                                    {approval &&
                                      approval.values[
                                        C.FIELD_ASSIGNED_TEAM_NAME
                                      ] && (
                                        <div>
                                          <dt>
                                            <I18n>Approval Teams</I18n>
                                          </dt>
                                          <dd>
                                            <I18n>
                                              {
                                                approval.values[
                                                  C.FIELD_ASSIGNED_TEAM_NAME
                                                ]
                                              }
                                            </I18n>
                                          </dd>
                                        </div>
                                      )}
                                    {approval.coreState === 'Submitted' &&
                                      approval.values['Approved'] ===
                                        'Denied' && (
                                        <div>
                                          <dt>
                                            <I18n>Reason</I18n>
                                          </dt>
                                          <dd>
                                            <I18n>
                                              {approval.values['Reason']}
                                            </I18n>
                                          </dd>
                                        </div>
                                      )}
                                  </dl>
                                </CardRow>
                              )}
                            </CardCol>
                          </Card>
                        );
                      })}
                    </div>
                  ) : (
                    <EmptyMessage title="A revision does not exist." />
                  )}
                </div>
              )}

              {mode === 'actions' && (
                <div className="nav-tabs--content">
                  {actions.size > 0 ? (
                    <div className="cards mb-4">
                      {actions.map(action => {
                        return (
                          <ActionCard
                            key={action.id}
                            submission={action}
                            path={getActionPath(appLocation, action)}
                            reload={() => {
                              fetchService(serviceId);
                            }}
                            external={true}
                          />
                        );
                      })}
                    </div>
                  ) : (
                    <EmptyMessage title="There are no change requests." />
                  )}
                </div>
              )}
            </>
          )
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state, props) => {
  const service =
    state.settingsService.current &&
    state.settingsService.current.values[C.FIELD_SERVICE_ID] === props.serviceId
      ? state.settingsService.current
      : null;
  const services = state.settingsService.data.get(props.serviceId) || List();
  return {
    // Current service
    service,
    serviceContentReady: service && service.values['Content'],
    serviceLoading: state.settingsService.currentLoading,
    serviceError: state.settingsService.currentError,
    // Revisions for this service
    revisions: services.filter(p => p.values['Status'] !== 'Active'),
    // Actions tied to this service that the user can access
    actions: List(
      service && service.children
        ? service.children.filter(
            c =>
              c.type === 'Action' &&
              (state.app.profile.isSMO ||
                c.values[C.FIELD_ASSIGNED_TEAMS].some(t =>
                  Utils.isMemberOf(state.app.profile, t),
                )),
          )
        : [],
    ),
    // All portfolios
    portfoliosNameMap: state.settingsPortfolio.dataList.reduce(
      (map, p) => map.set(p.values[C.FIELD_PORTFOLIO_ID], p.values['Name']),
      Map(),
    ),
    // Are any user actions are pending
    actionsPending: state.settingsService.actionsPending > 0,
    // Other state
    kappSlug: selectServicesKappSlug(state),
    profile: state.app.profile,
    isOwner: selectIsServiceOwner(state.app.profile, service),
    appLocation: state.app.location,
    layoutSize: state.app.layoutSize,
    isMobile: ['small', 'medium'].includes(state.app.layoutSize),
  };
};

const mapDispatchToProps = {
  fetchService: serviceActions.fetchServiceRequest,
  cloneService: serviceActions.cloneServiceRequest,
  expireService: serviceActions.expireServiceRequest,
  deleteServiceRevision: serviceActions.deleteServiceRevisionRequest,
};

export const Service = connect(
  mapStateToProps,
  mapDispatchToProps,
)(ServiceComponent);
